import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-toast-stack>` says about itself.
 *
 * Two facts about this piece are easy to miss from its name and both get an example.
 *
 * The first is `max`. It is not a scrollbar and not a "show more" — a stack over its cap REMOVES
 * the oldest toasts from the DOM, on every slotchange and on every attribute change. So the capped
 * example is declared with four toasts and draws two, and the code panel above it shows all four:
 * the difference between what was declared and what is on the stage is the demonstration.
 *
 * The second is that it flows IN PLACE by default and only pins itself to a corner when `float` is
 * set. That default is what lets it be composed inside any layout — and is why the floating case
 * needs an example of its own, held in a box, to be looked at at all.
 *
 * `float` and `position` are read only by the stylesheet and are not in `observedAttributes`; `max`
 * is the one that needs the element to do something when it changes. They are all three real inputs
 * and all three are declared, because a reader placing this piece does not care which of them
 * happens to require JavaScript.
 */
const docs: ComponentDocs = {
  aliases: ['toaster', 'toast container', 'snackbar container', 'notification area', 'notification region'],

  answers: [
    'put outcome notices in a corner of the screen',
    'give a surface one place for every toast it raises',
    'stop a burst of notifications growing without bound',
    'stack several notices with a consistent gap between them',
  ],

  // The frame's own notification corner: one per workspace, owned by the shell rather than by a
  // page, and it is there before any toast is. Declared narrowly for that reason — a stack per page
  // would mean two corners racing to hold the same notice.
  placements: ['seam'],

  inputs: [
    {
      name: 'max',
      attribute: 'max',
      type: 'number',
      description:
        'How many toasts may stay mounted. Anything past the cap is REMOVED oldest-first, so a burst ' +
        'cannot grow without bound. Unset (or zero) means uncapped.',
    },
    {
      name: 'float',
      attribute: 'float',
      type: 'boolean',
      description:
        'Pin the stack to a corner of the screen instead of flowing where it was put. The region ' +
        'itself becomes transparent to the pointer; the toasts inside it stay interactive.',
      default: false,
    },
    {
      name: 'position',
      attribute: 'position',
      type: 'text',
      options: ['bottom-right', 'bottom-left', 'top-right', 'top-left'],
      description:
        'Which corner to pin to when floating: bottom-right, bottom-left, top-right or top-left. A ' +
        'bottom corner also reverses the stacking order, so the newest toast is the one nearest the ' +
        'edge and the list reads bottom-up.',
      default: 'bottom-right',
    },
  ],

  // Declared as NONE rather than left unsaid: the stack raises nothing of its own. What a host
  // listens for is `caos-toast-dismiss`, which each toast raises and which composes up through here.
  events: [],

  state: [],

  slots: [{ name: '', takes: 'The toasts. Appending one is what "arrived" means; it is the newest.' }],

  tokens: [
    { token: '--caos-space-2', controls: 'The gap between one toast and the next.' },
    { token: '--caos-space-5', controls: 'How far a floating stack sits from the two screen edges of its corner.' },
  ],

  parts: [
    { part: 'region', is: 'the region the toasts stack in' },
  ],

  methods: [
    {
      name: 'push',
      description:
        'Mount a toast into the stack as the newest one. The same thing as appending it — a name for ' +
        'the operation, so a caller does not have to know that DOM order is arrival order.',
      args: [{ name: 'toast', type: 'CaosToast', description: 'The toast element to mount.' }],
    },
  ],

  placing:
    'One per workspace, mounted by the frame rather than dropped on a page, and mounted empty — it ' +
    'is there before anything has happened. Floating, it positions itself against the viewport and ' +
    'needs nothing around it. Flowing, it takes the width of whatever holds it and grows downward, ' +
    'so it wants a container that can afford to change height.',

  whenToUse:
    'A surface raises more than one transient notice and they need somewhere to go, in order, with a ' +
    'cap on how many can pile up. If a screen only ever raises one at a time and already has a place ' +
    'to put it, a Toast on its own is enough.',

  insteadUse: [
    { name: 'Toast', when: 'A single notice, mounted somewhere the surface already has room for it.' },
    {
      name: 'NoticeBanner',
      when:
        'The notice must not disappear — anything in this region can be dismissed, or pushed out by ' +
        'a later notice once the cap is reached.',
    },
  ],

  accessibility:
    'The region is a labelled role="region" with aria-live="polite", so a toast arriving is ' +
    'announced without interrupting — a danger toast overrides that with its own role="alert". A ' +
    'floating stack is transparent to the pointer except where a toast actually is, so it never eats ' +
    'a click meant for the page under it. What is left to the author: a toast dropped for exceeding ' +
    'max is removed silently, so the cap should be high enough that a person can plausibly have read ' +
    'what it displaces.',

  examples: [
    {
      key: 'stacked',
      label: 'Two notices, in place',
      summary: 'The default: the stack flows where it was put, spacing its toasts, so it composes inside any layout.',
      placement: {
        component: 'caos-toast-stack',
        inputs: { max: 3 },
        children: [
          { component: 'caos-toast', inputs: { intent: 'success' }, children: ['Order saved.'] },
          { component: 'caos-toast', inputs: { intent: 'warning' }, children: ['Two line items have no amount.'] },
        ],
      },
    },
    {
      key: 'capped',
      label: 'At the cap',
      summary:
        'Four toasts declared against a cap of two: the two oldest were removed, not hidden. Compare ' +
        'the code below with what is on the stage.',
      placement: {
        component: 'caos-toast-stack',
        inputs: { max: 2 },
        children: [
          { component: 'caos-toast', inputs: { intent: 'info' }, children: ['Totals refreshed.'] },
          { component: 'caos-toast', inputs: { intent: 'info' }, children: ['Draft saved.'] },
          { component: 'caos-toast', inputs: { intent: 'success' }, children: ['Order saved.'] },
          { component: 'caos-toast', inputs: { intent: 'warning' }, children: ['Two line items have no amount.'] },
        ],
      },
    },
    {
      key: 'floating',
      label: 'Pinned to a corner',
      summary:
        'The floating case, which is what a workspace actually uses: pinned to the top-right of the ' +
        'screen rather than flowing in the page.',
      placement: {
        component: 'caos-toast-stack',
        inputs: { float: true, position: 'top-right', max: 3 },
        children: [
          { component: 'caos-toast', inputs: { intent: 'success' }, children: ['Order saved.'] },
          { component: 'caos-toast', inputs: { intent: 'info' }, children: ['Totals refreshed from the rate table.'] },
        ],
      },
      // It positions itself against the viewport when floating, so it is held in its own box and
      // pins to the corner of THAT — which is how a corner-pinned piece can be looked at without
      // covering the page it is documented on.
      stage: { contain: 220 },
    },
  ],
};

export default docs;
